import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { CompanyInterface, CompanyModel } from '../../interfaces/company.interface';
import { InvitationSentDataType, UserInterface } from '../../interfaces/user.interface';
import { Pagination } from '@core/models/project.model';
import { TBResponse } from '@construction/models/wbs-elements.model';

interface CompanyQueryOptions {
  page?: number;
  pageSize?: number;
  isBuyer?: boolean;
  isSupplier?: boolean;
  isConstructionEpc?: boolean;
  isConstructionOwner?: boolean;
  isSupplierCompany?: boolean;
  disabled?: boolean;
}

export type SendInviteBodyType = {
  recipient_email: string;
  company_id: string;
  construction_project_ids: string[] | null;
  company_role: string;
  project_role: string;
};

export type FailedMailsType = {
  failed_emails: string[];
};

@Injectable({
  providedIn: 'root',
})
export class CompanyApiService {
  private http = inject(HttpClient);

  public getCompanies(options: CompanyQueryOptions = {}) {
    const params = new HttpParams({
      fromObject: {
        page: options.page || 1,
        page_size: options.pageSize || 15,
        ...(options.isBuyer !== undefined && { is_buyer: options.isBuyer }),
        ...(options.isSupplier !== undefined && { is_supplier: options.isSupplier }),
        ...(options.isConstructionEpc !== undefined && { is_construction_epc: options.isConstructionEpc }),
        ...(options.isConstructionOwner !== undefined && { is_construction_owner: options.isConstructionOwner }),
        ...(options.isSupplierCompany !== undefined && { is_supplier_company: options.isSupplierCompany }),
        ...(options.disabled !== undefined && { disabled: options.disabled }),
      },
    });

    return this.http.get<{ data: CompanyModel[]; pagination: Pagination }>(
      `${environment.apiUrl}/${environment.apiVersion}/companies/`,
      { params },
    );
  }

  public getSingleCompany(id: string): Observable<TBResponse<CompanyInterface>> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/companies/${id}/`;

    return this.http.get<TBResponse<CompanyInterface>>(url);
  }

  public getCompanySuccessGrade(id: string): Observable<any> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/successGrade/${id}/`;

    return this.http.get(url);
  }

  public getCompanyFinalizedDeals(id: string): Observable<any> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/finalizedDeals/${id}/`;

    return this.http.get(url);
  }

  public createCompany(data: FormData) {
    return this.http.post<TBResponse<CompanyInterface>>(
      `${environment.apiUrl}/${environment.apiVersion}/companies/`,
      data,
    );
  }

  public updateCompany(
    id: string,
    data: FormData,
    deleteLogoImage = false,
    deleteSiteImage = false,
  ): Observable<TBResponse<CompanyInterface>> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/companies/${id}/`;

    const params = new HttpParams({
      fromObject: {
        delete_logo_image: deleteLogoImage,
        delete_site_image: deleteSiteImage,
      },
    });

    return this.http.patch<TBResponse<CompanyInterface>>(url, data, { params });
  }

  public getCompanyContacts(companyId: string): Observable<any> {
    const params = new HttpParams({ fromObject: { page: 1, page_size: 1000 } });

    return this.http.get(`${environment.apiUrl}/${environment.apiVersion}/companies/${companyId}/users/`, { params });
  }

  public getCompanyUsers(
    companyId: string,
    page = 1,
    pageSize = 1000,
  ): Observable<{ data: UserInterface[]; pagination: Pagination }> {
    const params = new HttpParams({
      fromObject: {
        page,
        page_size: pageSize,
      },
    });

    return this.http.get<{ data: UserInterface[]; pagination: Pagination }>(
      `${environment.apiUrl}/${environment.apiVersion}/companies/${companyId}/users/`,
      { params },
    );
  }

  public getInvitations(
    companyId: string,
    page_size = 15,
  ): Observable<{ data: InvitationSentDataType[]; pagination: Pagination }> {
    const params = new HttpParams({ fromObject: { page_size } });

    return this.http.get<{ data: InvitationSentDataType[]; pagination: Pagination }>(
      `${environment.apiUrl}/${environment.apiVersion}/companies/${companyId}/invitations/`,
      { params },
    );
  }

  public deleteInvitation(id: string) {
    return this.http.delete(`${environment.apiUrl}/${environment.apiVersion}/invitations/${id}/`);
  }

  public sendInvitations(data: SendInviteBodyType) {
    return this.http
      .post<TBResponse<FailedMailsType>>(`${environment.apiUrl}/${environment.apiVersion}/invitations/`, data)
      .pipe(map((response) => response.data));
  }
}
